import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { useTransactionQuery } from 'apollo/queries/autogenerate/hooks';
import {
  Charge,
  ClawbackType,
  ItemKind,
} from 'apollo/queries/autogenerate/schemas';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import colors from 'shared/modules/common/constants/colors';
import AppleCarrierDiscountDetails from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/AppleCarrierDiscountDetails';
import AppleChargeDetails from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/AppleChargeDetails';
import AppleTradeInDetails from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/AppleTradeInDetails';

type Props = {
  charge: Charge;
  onCloseButtonPress: () => void;
  open: boolean;
};

export default function AppleChargeDetailsDialog(props: Props) {
  const charge = props.charge;
  const transactionId = charge.metadata.transactionId;
  const transactionItemId = charge.metadata.transactionItemId;

  const { data } = useTransactionQuery({
    variables: {
      id: transactionId ?? '',
    },
    fetchPolicy: 'cache-and-network',
  });

  if (!data) {
    return null;
  }

  const transactionItem = data?.transaction.items.find(
    (item) => item.id === transactionItemId
  );

  if (!transactionItem) {
    return null;
  }

  const isSale = transactionItem?.itemKind === ItemKind.SALE;
  const isCarrierDiscountClawback =
    transactionItem.clawbackType === ClawbackType.CARRIER_DISCOUNT || false;

  const getDialogTitle = () => {
    // If !isSale, then must be a clawback
    if (!isSale) {
      return isCarrierDiscountClawback
        ? '通信キャリア割引調整額'
        : '下取り調整差額';
    }

    return 'ご利用明細';
  };

  return (
    <Dialog
      title={getDialogTitle()}
      onCloseButtonPress={props.onCloseButtonPress}
      open={props.open}
      style={styles.container}
      id="apple-charge-details-dialog"
    >
      <View style={styles.body}>
        {isSale && (
          <AppleChargeDetails
            charge={charge}
            transactionItem={transactionItem}
          />
        )}
        {!isSale && !isCarrierDiscountClawback && (
          <AppleTradeInDetails
            charge={charge}
            transactionItem={transactionItem}
          />
        )}
        {!isSale && isCarrierDiscountClawback && (
          <AppleCarrierDiscountDetails
            charge={charge}
            transactionItem={transactionItem}
          />
        )}
      </View>
    </Dialog>
  );
}

export function useAppleChargeDetailsDialog() {
  const [charge, setCharge] = useState<Charge>();
  const [isOpen, setIsOpen] = useState(false);

  const open = (_charge: Charge) => {
    setCharge(_charge);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return {
    charge,
    isOpen,
    open,
    close,
  };
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 424,
    backgroundColor: colors.white,
    paddingBottom: 0,
  },
  body: {
    marginHorizontal: -24,
    backgroundColor: '#f6f8fa',
  },
});
